/**
 * Fonte ÚNICA de resolução `rótulo do GHL` → `slug de produto`.
 *
 * Antes deste arquivo existiam três cópias byte-a-byte da mesma regra — `normalizeProduct`
 * (`api.js`, caminho de ESCRITA), `resolverSlugProduto` (`pontosConfig.js`, usado pela tela e
 * por `blindarPontosPendentes`) e `normalizeProductMig` (`MetasPontuacao.jsx`, migração em
 * lote) — mais uma quarta em teste. Três cópias significam três chances de divergir do que a
 * escrita realmente faz, e o que a tela mostra deixa de ser o que o sistema paga.
 *
 * ── O defeito que motivou o módulo (medido em produção, 2026-08-31) ──────────────────────
 * A regra antiga era: minusculiza o rótulo, devolve o primeiro produto cuja *keyword* seja
 * SUBSTRING do rótulo, e — sem match — cai no ÚLTIMO produto da lista. Consequências reais:
 *
 *  - `'tra'` (keyword de `tracao`) é substring de "con**tra**to", "es**tra**tégia" e
 *    "Hora Ex**tra**": os três resolvem para `tracao`.
 *  - `pcl` e `treinamentos_orbion` têm `keywords: []`, então "PCL" e "E-commerce" NÃO casam
 *    com ninguém e caem no fallback — sendo pagos com o override do último da lista.
 *  - Acento assimétrico: `'expans'` casa com "Expansão" (o acento vem depois do prefixo), mas
 *    `'tracao'` NÃO casa com "Tração" — `'tração'.includes('tracao') === false`.
 *
 * ── A regra nova ─────────────────────────────────────────────────────────────────────────
 * `resolverProduto` casa por IGUALDADE do rótulo normalizado (sem acento, minúsculo, trim)
 * contra os `aliases` declarados do produto, o `label` e o `slug`. Sem match, RECUSA:
 * `{ slug: null, motivo: 'nao_resolve' }`. Não existe fallback aqui — rótulo que não resolve
 * deve ser recusado, nunca adivinhado. O sistema mostra números; número adivinhado na tela é
 * exatamente o risco que se quer eliminar.
 *
 * ── Retrocompatibilidade ─────────────────────────────────────────────────────────────────
 * Produto que ainda NÃO declara `aliases` é tratado como config antiga: casa por substring de
 * `keywords`, exatamente como antes (inclusive sem remover acento — a comparação legada era
 * `toLowerCase()` puro e precisa continuar byte-a-byte igual), e o motivo sai como
 * `'keyword_legado'` para que dê para MEDIR quanto do tráfego ainda depende da regra velha.
 * Produto legado também não ganha igualdade por `label`/`slug`: dar isso a ele mudaria o
 * comportamento de hoje (`"PCL"` passaria a resolver para `pcl` em vez do fallback), e este
 * módulo, no Deploy A, não pode mudar nada de observável.
 */

const DIACRITICOS = /[\u0300-\u036f]/g;

/** Forma canônica de comparação: sem espaço nas pontas, minúscula, sem diacrítico. */
export function normalizarRotulo(valor) {
    return String(valor ?? '').trim().toLowerCase().normalize('NFD').replace(DIACRITICOS, '');
}

/**
 * Forma LEGADA, usada só no caminho de `keywords`: `toLowerCase()` puro, sem trim e sem
 * normalizar acento. Não "melhorar" — é ela que garante que o modo sombra devolva o mesmo
 * slug que produção devolve hoje.
 */
function rotuloLegado(valor) {
    return String(valor || '').toLowerCase();
}

/** Config nova = a que declara `aliases` (mesmo vazio). Config antiga = a que só tem `keywords`. */
function ehConfigNova(produto) {
    return Array.isArray(produto?.aliases);
}

/** Chaves de igualdade de um produto, na ordem de precedência: aliases → label → slug. */
function chavesDeIgualdade(produto) {
    const chaves = (produto.aliases ?? []).map((a) => ({ valor: a, motivo: 'alias' }));
    if (produto?.label) chaves.push({ valor: produto.label, motivo: 'label' });
    if (produto?.slug) chaves.push({ valor: produto.slug, motivo: 'slug' });
    return chaves;
}

/**
 * Resolve um rótulo de produto SEM inventar resposta.
 *
 * @param {unknown} rotulo   Rótulo cru como veio do GHL (acentuado, com maiúsculas).
 * @param {Array<{slug: string, label?: string, aliases?: string[], keywords?: string[]}>} produtos
 * @returns {{slug: string|null, motivo: string, alias?: string}}
 *   `motivo` ∈ `'alias' | 'label' | 'slug' | 'keyword_legado' | 'sem_slug' | 'nao_resolve'`.
 *   `alias` é o token que produziu o match (útil para auditar de onde veio a decisão).
 */
export function resolverProduto(rotulo, produtos) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const alvo = normalizarRotulo(rotulo);
    const alvoLegado = rotuloLegado(rotulo);

    for (const produto of lista) {
        if (ehConfigNova(produto)) {
            // Rótulo vazio nunca casa: `chave` vazia é descartada de propósito, senão um
            // produto com alias `''` absorveria todo rótulo em branco.
            if (!alvo) continue;
            const achou = chavesDeIgualdade(produto)
                .find(({ valor }) => normalizarRotulo(valor) && normalizarRotulo(valor) === alvo);
            if (achou) {
                return produto.slug
                    ? { slug: produto.slug, motivo: achou.motivo, alias: achou.valor }
                    : { slug: null, motivo: 'sem_slug', alias: achou.valor };
            }
        } else {
            const keywords = Array.isArray(produto?.keywords) ? produto.keywords : [];
            // `includes` com a keyword CRUA — é o que a regra antiga fazia.
            const keyword = keywords.find((k) => alvoLegado.includes(k));
            if (keyword !== undefined) {
                return produto.slug
                    ? { slug: produto.slug, motivo: 'keyword_legado', alias: keyword }
                    : { slug: null, motivo: 'sem_slug', alias: keyword };
            }
        }
    }

    return { slug: null, motivo: 'nao_resolve' };
}

/**
 * @deprecated Existe SÓ para o modo sombra do Deploy A: reproduz o comportamento ANTIGO,
 * incluindo o `lista.at(-1)` que faz todo rótulo desconhecido ser pago como o último produto
 * da lista. Não usar em código novo — quem precisa da verdade usa `resolverProduto`, que
 * recusa em vez de adivinhar.
 *
 * Diferença deliberada em relação a `normalizeProduct` de `api.js`: aqui NÃO existe o
 * `?? 'pcl'` nem a troca por `DEFAULT_PRODUTOS` quando a lista vem vazia. Esses dois defaults
 * pertencem ao chamador — cada um tem o seu, e eles não são iguais (ver `pontosConfig.js`).
 *
 * @returns {{slug: string|null, motivo: string, alias?: string}} com `motivo: 'fallback'`
 *   quando o slug veio do último da lista — é esse motivo que os chamadores logam.
 */
export function resolverProdutoOuFallback(rotulo, produtos) {
    const lista = Array.isArray(produtos) ? produtos : [];
    const resultado = resolverProduto(rotulo, lista);
    if (resultado.slug != null) return resultado;

    const ultimo = lista.at(-1);
    if (ultimo?.slug) return { slug: ultimo.slug, motivo: 'fallback', alias: null };
    return { slug: null, motivo: 'nao_resolve' };
}

/**
 * Log único do modo sombra. Prefixo `[produto]` para dar para filtrar no console e no Sentry
 * quantos rótulos por dia ainda dependem de adivinhação — é o número que decide quando o
 * Deploy B pode passar a RECUSAR.
 */
export function avisarSeAdivinhou(origem, rotulo, resultado, slugFinal) {
    if (resultado.motivo !== 'fallback' && resultado.slug != null) return;
    console.warn(
        `[produto] ${origem}: rótulo ${JSON.stringify(rotulo)} não resolve por si — ` +
        `usando ${JSON.stringify(slugFinal ?? resultado.slug)} (motivo: ${resultado.motivo})`,
    );
}
